import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { Department, IDepartmentPost } from "../models/department";
import { DepartmentEmployee } from "../models/departmentEmployee";
import { convertDateOnlyToUtc } from "../utils/dateUtils";

class DepartmentService {
  /*******************************
   *          DEPARTMENT
   *******************************/

  async createDepartments(department: IDepartmentPost): Promise<Department> {
    return new Promise((resolve, reject) => {
      axios
        .post<Department>(configService.APIInstitutionUrl("departments.json"), department)
        .then((response: AxiosResponse) => {
          resolve(Department.fromJSON(ObjectFromEnvelope<Department>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async deleteDepartments(departmentId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Department>(
          configService.APIInstitutionUrl("departments/" + departmentId + ".json")
        )
        .then(() => {
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  async updateDepartments(department: Department): Promise<Department> {
    return new Promise((resolve, reject) => {
      axios
        .put<Department>(
          configService.APIInstitutionUrl("departments/" + department.id + ".json"),
          department.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          resolve(Department.fromJSON(ObjectFromEnvelope<Department>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }

  async fetchDepartments(): Promise<Department[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Department[]>(configService.APIInstitutionUrl("departments.json"), {
          params: {
            institution_id: configService.institutionId()
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Department.fromJSONArray(ArrayFromEnvelope<Department>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /*******************************
   *     DEPARTMENT EMPLOYEES
   *******************************/

  async fetchDepartmentEmployees(): Promise<DepartmentEmployee[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<DepartmentEmployee[]>(configService.APIInstitutionUrl("department_employees.json"), {
          params: {
            institution_id: configService.institutionId()
          }
        })
        .then((response: AxiosResponse) => {
          resolve(
            DepartmentEmployee.fromJSONArray(ArrayFromEnvelope<DepartmentEmployee>(response.data))
          );
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async createDepartmentEmployee(
    departmentId: number,
    departmentEmployeeId: number,
    primaryDepartment: boolean
  ): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      const de: DepartmentEmployee = new DepartmentEmployee();
      de.department_id = departmentId;
      de.employee_id = departmentEmployeeId;
      de.primary_department = primaryDepartment;
      axios
        .post<DepartmentEmployee>(configService.APIInstitutionUrl("department_employees.json"), de)
        .then((response: AxiosResponse) => {
          resolve(
            DepartmentEmployee.fromJSON(ObjectFromEnvelope<DepartmentEmployee>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }

  async updateDepartmentEmployee(
    departmentEmployeeId: number,
    primaryDepartment: boolean
  ): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      axios
        .put<DepartmentEmployee>(
          configService.APIInstitutionUrl("department_employees/" + departmentEmployeeId + ".json"),
          { primary_department: primaryDepartment }
        )
        .then((response: AxiosResponse) => {
          resolve(
            DepartmentEmployee.fromJSON(ObjectFromEnvelope<DepartmentEmployee>(response.data))
          );
        })
        .catch((error) => reject(error));
    });
  }

  async deleteDepartmentEmployee(departmentEmployeeId: number): Promise<DepartmentEmployee> {
    return new Promise((resolve, reject) => {
      axios
        .delete<DepartmentEmployee>(
          configService.APIInstitutionUrl("department_employees/" + departmentEmployeeId + ".json")
        )
        .then((response: AxiosResponse) => {
          resolve(response.data as any);
        })
        .catch((error) => reject(error));
    });
  }

  /*******************************
   *     BULK TRANSFER DUTIES
   *******************************/

  async bulkTransferDepartments(
    transferDate: Date,
    employeeTransfers: Array<{
      employee_id: number;
      from_department_id: number;
      to_department_id: number | null;
    }>
  ): Promise<{ success: boolean; moved_duties_count: number }> {
    return new Promise((resolve, reject) => {
      axios
        .post(configService.APIInstitutionUrl("duties/bulk_transfer_departments.json"), {
          transfer_date: convertDateOnlyToUtc(transferDate),
          employee_transfers: employeeTransfers
        })
        .then((response: AxiosResponse) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
}

export const departmentService: DepartmentService = new DepartmentService();
